import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Button,
  Input,
  Avatar,
  Tooltip,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '@heroui/react';
import {
  HomeIcon,
  UsersIcon,
  SparklesIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightStartOnRectangleIcon,
  ChevronUpIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  LockClosedIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '@stores/authStore';
import { useStoreManagerUnread } from '@hooks/useStoreManagerUnread';
import { useAgentAttention } from '@hooks/useAgentAttention';
import {
  getUnlockedAgentSlugs,
  getUnlockedAgentTypes,
  getPlanTierFromPaymentStatus,
  effectivePlanStatus,
} from '@lib/services/payment/planUtils';
import { PLAN_LABEL, planKeyForTier } from '../../config/creditAgents';
import MayaAvatar from '../../assets/employees/v2/face/maya.png';
import AnnaAvatar from '../../assets/employees/v2/face/anna.png';
import OscarAvatar from '../../assets/employees/v2/face/oscar.png';
// design 23754 — the exact sidebar icons, exported from Figma (stroke → currentColor). Level-1 nav
// rows carry these; the level-2 agent rows carry avatars instead (see nav items below).
import {
  StoreManagerNavIcon,
  MarketingNavIcon,
  SupportNavIcon,
  OperationNavIcon,
  BoxNavIcon,
  SettingsNavIcon,
  TaskCenterNavIcon,
  ApprovalsNavIcon,
  MaterialNavIcon,
} from './navIcons';
import { useThemeStore } from '@stores/themeStore';
import { useIsIOS } from '@hooks/useMediaQuery';
import { useSafeViewportHeight } from '@hooks/useSafeViewportHeight';
import YepAILogo from '../../assets/logo.png';
import HomeBgImage from '../../assets/Home_bg.png';
import { UpgradePlanCard } from './UpgradePlanCard';
import { useCreditStore } from '../../stores/creditStore';
import { usePaymentStore } from '../../stores/paymentStore';
import { useCustomisationStore } from '@stores/customisationStore';

interface NavigationItem {
  id: string;
  name: string;
  href?: string;
  icon: React.ElementType;
  current?: boolean;
  children?: NavigationItem[];
  badge?: string;
  /**
   * Red dot for "there is something new in here" (#5.5 / PRD V1.23).
   *
   * Separate from {@link badge}: the badge is a static label the code decides once ("NEW" on a feature
   * that just shipped), where this tracks live unread state and has to disappear the moment the
   * merchant reads it. Rendering one as the other would leave a NEW badge on a two-month-old feature,
   * or a dot that never clears.
   */
  unreadDot?: boolean;
  /**
   * Router state to navigate with, so a dot can carry WHICH thing it is about (#6).
   *
   * An agent row's href is one fixed page, which is why clicking a red dot used to land the merchant
   * in a blank new conversation: the destination knew an agent needed attention but not what for.
   * The target page reads this (see `ContentStudioPage`) and opens that conversation.
   */
  navState?: Record<string, unknown>;
  /** Top-level grouping header (e.g. "Support", "Marketing") —
   *  rendered with a coloured icon chip + bold label, expand/collapse only. */
  isGroup?: boolean;
  /**
   * A pure section LABEL, not a collapsible group (design 23157: "AI TEAM" / "PLATFORM").
   * It is a name only — a grey uppercase heading, never clickable, never toggling. Its children are
   * the real nav rows, promoted to the top level (depth 0) and always visible, so they read as
   * primary items with their own icons/avatars rather than sitting inside a group the merchant has
   * to open. Distinct from {@link isGroup}, which IS a clickable/collapsible row.
   */
  isSectionLabel?: boolean;
  /** Agent avatar shown in place of the group icon chip (Maya / Anna). */
  avatar?: string;
  /** Coloured circle background behind the agent avatar. */
  avatarBg?: string;
  /** CSS transform to re-centre an off-centre face in the avatar (e.g. Maya). */
  avatarTransform?: string;
  /** Plan-gated: render a lock badge + "Join your team soon" on the header. */
  locked?: boolean;
  /** Non-navigating placeholder row (e.g. Marketing "Coming soon"). */
  disabled?: boolean;
  /** Temporarily omit this item (and its descendants) without removing its route/configuration. */
  hidden?: boolean;
}

export function DashboardSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const creditOverview = useCreditStore(s => s.overview);
  const fetchCreditOverview = useCreditStore(s => s.fetchOverview);
  // Cohort drives the legacy "gifted Growth" unlock — fetched globally here so
  // the Customer lock resolves even before the user visits a billing page.
  const cohort = usePaymentStore(s => s.cohort);
  const fetchVisiblePlans = usePaymentStore(s => s.fetchVisiblePlans);
  const { sidebarCollapsed, toggleSidebar, setSidebarCollapsed, searchQuery, setSearchQuery } =
    useThemeStore();
  // Groups start collapsed; the effect below opens whichever group matches the route.
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [buttonPosition, setButtonPosition] = useState<{ top: number; left: number } | null>(null);
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Credit balance for the profile card (new-plan / credit-billed users only).
  useEffect(() => {
    if (user?.id) {
      fetchCreditOverview(user.id);
      fetchVisiblePlans(user.id);
    }
  }, [user?.id, fetchCreditOverview, fetchVisiblePlans]);

  // Price Plan v2 — which agents the user's plan unlocks (Maya always; Anna for
  // Growth+, Alex for Pro+). Legacy-cohort users are gifted Growth (Anna), so
  // the Customer workspace stays unlocked for them.
  /**
   * Agent TYPES, alongside the slugs below: `operation` has no roster slug, so the slug set can
   * express "Anna is locked" but never "Oscar is locked".
   */
  const unlockedAgentTypes = useMemo(
    () => getUnlockedAgentTypes(effectivePlanStatus(user), cohort),
    [user, cohort]
  );
  const unlockedAgents = useMemo(
    () => getUnlockedAgentSlugs(effectivePlanStatus(user), cohort),
    [user, cohort]
  );

  // Anna's sidebar avatar is her own FIXED portrait (merchant feedback 2026-08-21): the Customer
  // workspace identity must not change when the merchant swaps the shopper-facing web-chat avatar.
  // (Previously this mirrored `settings.avatarName`, same as Home / the AI Team card.)
  const loadCustomisation = useCustomisationStore(s => s.loadSettings);
  useEffect(() => {
    void loadCustomisation();
  }, [loadCustomisation]);
  const customerAvatar = AnnaAvatar;

  // Profile-popup plan label + credit balance.
  const isLegacy = cohort === 'LEGACY';
  // NEW-cohort users only have a plan tier when they hold an active/trialing credit
  // subscription (creditPlanCode). Without one (never subscribed, or a cancelled
  // trial), don't fall back to paymentStatus — that's always "unpaid" for ai_* and
  // would mislabel them as "Core". Show "Free" instead. Legacy keeps its mapping.
  const planLabel = isLegacy
    ? PLAN_LABEL[planKeyForTier(getPlanTierFromPaymentStatus(effectivePlanStatus(user), cohort))]
    : user?.inFreeTrial
      ? 'Free trial'
      : !user?.creditPlanCode || user.creditPlanCode === 'free'
        ? 'Free'
        : PLAN_LABEL[
            planKeyForTier(getPlanTierFromPaymentStatus(effectivePlanStatus(user), cohort))
          ];
  const availableCredits = Math.round(creditOverview?.availableCredits ?? 0);

  // #5.5 / PRD V1.23: the Store Manager's red dot. A SHARED hook, so this and the Home Workspace
  // card can never show different unread states — two dots disagreeing about "something is new" reads
  // as a bug in the very feature meant to tell the merchant what is new.
  const storeManagerUnread = useStoreManagerUnread();
  // PRD #4 (YEP-778 / YEP-803 / YEP-805): the sidebar's red dots. A dot means「有你没看过的东西」—
  // an unread task or an unread chat under that agent — NOT「你该动手了」, which is what the status
  // labels and the filter counts are for. Deliberately not the same source as the Home Workspace
  // card's status point: 状态点答「现在怎么了」, 红点答「有没有没看过的」, and the PRD says the two
  // do not synchronise.
  const attention = useAgentAttention();

  /**
   * What to hand the destination page so the dot lands on the failure itself (#6).
   *
   * Only for a FAILURE with a known conversation. An item awaiting the merchant is a 待处理 task in
   * the Task Center (#19), not a chat; and a scheduled deliverable ran in no conversation at all —
   * for both, returning nothing leaves the row navigating to its normal page, which is where the
   * merchant should go.
   *
   * `ackTaskId` travels with it because reading the failure is what dismisses the dot, and the page
   * that shows it is the only place that knows the merchant got there.
   */
  const attentionNavState = useCallback(
    (agentType: string): Record<string, unknown> | undefined => {
      const target = attention.targetByAgent?.[agentType];
      if (
        !attention.attentionByAgent[agentType] ||
        !target?.failed ||
        !target.conversationId
      )
        return undefined;
      return { openConversationId: target.conversationId, ackTaskId: target.taskId ?? null };
    },
    [attention.attentionByAgent, attention.targetByAgent]
  );

  // Check if user has Shopify installation
  const hasShopifyInstallation = useMemo(() => {
    return (
      user?.installations?.some(
        installation => installation.platform?.toLowerCase() === 'shopify'
      ) || false
    );
  }, [user?.installations]);

  // Close sidebar on mobile when navigating
  const handleNavigation = (href: string, navState?: Record<string, unknown>) => {
    // Two calls rather than one with `undefined`: every row but the dotted ones navigates exactly as
    // it did before, argument for argument.
    if (navState) navigate(href, { state: navState });
    else navigate(href);
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 1024) {
      setSidebarCollapsed(true);
    }
  };

  // Auto-expand menus based on current location
  useEffect(() => {
    // Expand a child group, always keeping the Support group AND its new 'ai-team' parent open:
    // the agents are nested one level deeper now, so opening only the leaf group would leave it
    // invisible inside a collapsed AI Team.
    const expand = (ids: string[]) =>
      setExpandedItems(prev => [...new Set([...prev, 'ai-team', 'customer-sales', ...ids])]);

    if (location.pathname.startsWith('/ai-training')) expand(['ai-training']);
    if (location.pathname.startsWith('/customers')) expand(['customers']);
    // Live Chat → WhatsApp page: keep the parent group open so it stays visible.
    // Content Studio and the Marketing Hub live under the Marketing group. (Brand IQ used to as
    // well; it is a global setting now — see the Platform group below.)
    if (location.pathname.startsWith('/ai-team/marketing/')) {
      setExpandedItems(prev => [...new Set([...prev, 'ai-team', 'marketing'])]);
    }
    // Cross-agent pages live under the Platform group. Without this the group stays shut on a
    // direct hit — and Brand IQ's old URLs redirect straight to /platform/settings, so the merchant
    // would land on the Hub with no nav cue for where they are.
    if (location.pathname.startsWith('/platform/') || location.pathname === '/tasks') {
      setExpandedItems(prev => [...new Set([...prev, 'platform'])]);
    }
    // Assistant Chatbot lives under the Operation group.
    if (location.pathname.startsWith('/ai-team/operation')) {
      setExpandedItems(prev => [...new Set([...prev, 'ai-team', 'operation'])]);
    }
    // No case for '#operation-settings': that hash used to have its own row nested under AI Team →
    // Operation, which had to be opened or the highlight sat inside a collapsed group. The row is gone
    // and Platform → Settings — already opened above — owns the path outright.
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      // Use setTimeout to ensure state cleanup completes before navigation
      setTimeout(() => {
        navigate('/auth/login');
      }, 0);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Filter navigation items based on search query (recurses through all levels).
  const visibleNavigationItems = (items: NavigationItem[]): NavigationItem[] =>
    items
      .filter(item => !item.hidden)
      .map(item => ({
        ...item,
        children: item.children ? visibleNavigationItems(item.children) : undefined,
      }));

  const filterNavigationItems = (items: NavigationItem[]): NavigationItem[] => {
    const visibleItems = visibleNavigationItems(items);
    if (!searchQuery.trim()) return visibleItems;

    const query = searchQuery.toLowerCase();
    return visibleItems
      .map(item => {
        const mainMatches = item.name.toLowerCase().includes(query);
        const filteredChildren = item.children ? filterNavigationItems(item.children) : [];

        // Keep the item if it matches, or if any descendant matches.
        if (mainMatches || filteredChildren.length > 0) {
          return {
            ...item,
            // When the item itself matches, keep its full subtree; otherwise
            // only the matching branch.
            children: mainMatches ? item.children : filteredChildren,
          };
        }
        return null;
      })
      .filter(Boolean) as NavigationItem[];
  };

  // YEP-803 #1/#4: a stable signature of every value that feeds a red dot, so the nav memo recomputes
  // when a dot lights or clears (the memo keys on pathname etc., not on these hook objects, which are
  // fresh each render). Without it the section-label aggregate would read stale child dots.
  const unreadSignature = `${storeManagerUnread}|${attention.taskCenterAttention}|${Object.keys(
    attention.attentionByAgent
  )
    .filter(k => attention.attentionByAgent[k])
    .sort()
    .join(',')}`;

  const navigation: NavigationItem[] = useMemo(
    () => [
      {
        id: 'home',
        name: 'Home',
        // dev serves the Home page at /home (the index route '/' redirects to
        // /ai-team), so link here rather than to '/'.
        href: '/home',
        icon: HomeIcon,
        current: location.pathname === '/home',
      },
      {
        // The team's section HEADING, not a group (design 23157: "AI TEAM" is a label, not a
        // collapsible row). Its children — Store Manager and the agents — sit directly beneath it as
        // primary rows, always visible. Marketing / Operation / Support read as members of the team
        // by living under this heading, without the merchant having to open an "AI Team" container.
        //
        // The heading is still a section LABEL (not a collapsible container), but clicking it DOES
        // navigate to the AI Team roster page — that entry point was lost when the roster row was
        // removed, and merchants expect the section title to take them to the team overview. `href`
        // makes only this label clickable; PLATFORM has no roster page and stays a plain label.
        id: 'ai-team',
        name: 'AI Team',
        href: '/ai-team',
        icon: UserGroupIcon,
        isSectionLabel: true,
        children: [
        {
          // #5.1: FIRST in the list. It leads because it is the one entry that aggregates the others
          // — a merchant opening the team should land on "what happened" before "who did it". Not an
          // agent, though: the Store Manager is a program (no config card, not in the Integrations
          // matrix), which is why it has no avatar and no children.
          id: 'store-manager',
          name: 'Store Manager',
          href: '/store-manager',
          icon: StoreManagerNavIcon,
          // PRD #4 (YEP-778): NEW badges are gone; the sidebar carries ONE marker, a red dot meaning
          // "something here needs you". PRD V1.23 wants it on first open (the setup nudge is waiting)
          // and AGAIN when the congrats lands — both "unread", so one signal covers both.
          unreadDot: storeManagerUnread > 0,
          current: location.pathname === '/store-manager',
        },
        {
          id: 'marketing',
          name: 'Marketing',
          // design 23754: the level-1 group header is an ICON (target-04), not the agent's face —
          // the avatar belongs on Maya's own second-level row below.
          icon: MarketingNavIcon,
          isGroup: true,
          // Prototype alignment: Marketing has two entries — Maya · Chat (the
          // conversation, formerly "Content Studio") and Marketing Hub. Image
          // Studio is no longer a top-level nav item.
          children: [
            {
              id: 'maya-chat',
              name: 'Maya',
              href: '/ai-team/marketing/studio',
              // design 23754: the agent's chat row carries the agent AVATAR (Maya's face).
              icon: ChatBubbleLeftRightIcon,
              avatar: MayaAvatar,
              avatarBg: '#d1fae5',
              avatarTransform: 'translateX(7%) translateY(11%) scale(1.2)',
              // YEP-790: the failed / awaiting-approval dot belongs on Maya's own row (the
              // second-level menu), not the Marketing group above it.
              unreadDot: attention.attentionByAgent['marketing'],
              // #6: and clicking it opens the conversation the failure happened in. Only Maya's page
              // consumes this so far — Oscar's and Anna's chats have no equivalent handler, and
              // handing them a state nobody reads would be a dot that still lands nowhere while
              // looking wired up.
              navState: attentionNavState('marketing'),
              current: location.pathname === '/ai-team/marketing/studio',
            },
            {
              id: 'marketing-hub',
              name: 'Marketing Hub',
              href: '/ai-team/marketing/hub',
              icon: BoxNavIcon,
              current: location.pathname.startsWith('/ai-team/marketing/hub'),
            },
          ],
        },
        // Operation (Oscar) 分组跟着解锁走，不再自己判环境（原来是 appEnv === 'development'）：
        // dev 上 planUtils 的 OPERATION_OPEN_ON_DEV 让全员解锁，效果与以前一致；其余环境只有
        // 解锁了 operation 的套餐看得见，没解锁的整组隐藏。（port 自 main 5ab92e20）
        ...(unlockedAgentTypes.has('operation')
          ? [
              {
                id: 'operation',
                name: 'Operation',
                // design 23754: level-1 header is the ICON (dataflow-03); Oscar's face is on his row.
                icon: OperationNavIcon,
                // Oscar is a Pro agent. The AI Team page already showed his card as Locked while
                // this group sat open with three working sub-pages (Monica, 2026-08-12): the slug
                // set this sidebar gated on has no entry for `operation` at all, so nothing here
                // could ever lock him.
                locked: !unlockedAgentTypes.has('operation'),
                isGroup: true,
                // Prototype alignment: Operation has three entries — Oscar (the
                // conversation), Product Management (selection/store/report tabs)
                // and Setting.
                children: [
                  {
                    id: 'operation-chat',
                    name: 'Oscar',
                    // Oscar now opens on his landing/home page; the chat is reached
                    // from there. Highlight the entry for both home and chat routes.
                    href: '/ai-team/operation',
                    // design 23754: Oscar's chat row carries his AVATAR.
                    icon: ChatBubbleLeftRightIcon,
                    avatar: OscarAvatar,
                    avatarBg: '#e0e7ff',
                    // YEP-790: dot on Oscar's own row, not the Operation group.
                    unreadDot: attention.attentionByAgent['operation'],
                    current:
                      location.pathname === '/ai-team/operation' ||
                      location.pathname === '/ai-team/operation/chat',
                  },
                  {
                    id: 'operation-pm',
                    // [需求1a] 页面 H1 已由「Product Management」改名为「Operation Hub」,
                    // 侧边栏菜单项同步改名(纯文案,不动 id/href/路由)。
                    name: 'Operation Hub',
                    href: '/ai-team/operation/product-management',
                    icon: BoxNavIcon,
                    current: location.pathname === '/ai-team/operation/product-management',
                  },
                  // No "Setting" row: it only ever pointed at Platform → Settings, where Oscar's card
                  // already lives beside every other agent's. Anna has no such row either, and a
                  // shortcut that lands on the page the row below it opens is a second name for one
                  // destination — it also forced this row and Platform → Settings to negotiate over
                  // which of them got to look active.
                ],
              },
            ]
          : []),
        {
          id: 'customer-sales', // id kept stable (expanded-state); label renamed to Figma's "Support" (CS-08)
          name: 'Support',
          // design 23754: level-1 header is the ICON (message-chat-circle); Anna's face is on her row.
          icon: SupportNavIcon,
          locked: !unlockedAgents.has('anna'),
          isGroup: true,
          children: [
            /*
             * PRD V1.4：Support 分組僅保留 Anna Chat 與 Leads。这一版把其余六项收走了。
             *
             * 每一项都在别处有了正主，留着就是第二扇门——而两扇门通向同一处配置，迟早
             * 会各自漂移：
             *   AI Training / Product Training → 页面仍在（Settings 里还没有等价物），
             *     只是不再从这里进；知识库的 ConnectedSourceCard 仍会带商家过去。
             *   Customisation                  → Settings Hub 的 Customer 卡
             *   Live Chat                      → Settings Hub 的 Integrations 卡
             *   Conversation History           → Anna 页左栏那一列客户对话
             *   Customers 这一层                → 只剩 Leads 一个孩子，包一层没有意义
             *
             * 被收走的路径都改成重定向而不是删掉，和 Brand IQ / Channels / Preview & Test
             * 的退役方式一致——商家的书签不该落到 404 上。
             */
            {
              id: 'anna-chat',
              // 就叫 Anna：这一栏里她是个人，不是一个功能模块。
              name: 'Anna',
              href: '/ai-team/anna/chat',
              // design 23754: Anna's chat row carries her AVATAR.
              icon: ChatBubbleLeftRightIcon,
              avatar: customerAvatar,
              avatarBg: '#fce7f3',
              // YEP-790: dot on Anna's own row, not the Support group. Anna is agentType `concierge`.
              unreadDot: attention.attentionByAgent['concierge'],
              current: location.pathname === '/ai-team/anna/chat',
            },
            {
              id: 'leads',
              name: 'Leads',
              href: '/customers/leads',
              icon: UsersIcon,
              current: location.pathname === '/customers/leads',
            },
            // The Analytics submenu (Overview / Support Performance / Chat Summary / Busiest Time)
            // is gone: PRD #2 moved all four into Home's Customer Data tab, 整体搬入. Leaving the
            // entries here as well would give the merchant two doors to the same charts, and the
            // old URLs redirect to the matching sheet rather than 404.
          ],
        },
        ],
      },
      {
        // Cross-agent pages (#22/#23 task centre). A group of their own because they are not any
        // one agent's: they read agent-neutral endpoints and aggregate across Marketing, Customer
        // and Operation. Filing them under an agent would imply an ownership that does not exist,
        // and the merchant would go looking for Oscar's tasks under Oscar.
        //
        // [Infra 1.5 #19] Three rows, not four: 审批中心废除，Approvals 入口删除，相关事项一律
        // 变成任务中心的「待处理」任务。09-02 的 hotfix 只是给那一行加了 hidden 挡渲染,
        // 这里直接把行删掉 —— 路由也一并移除（见 router/index.tsx）。
        //
        // The second section HEADING (design 23157: "PLATFORM" is a label like "AI TEAM"). Its rows —
        // Settings / Task Center / Approvals / Material Hub — sit directly beneath it, always visible,
        // rather than inside a collapsible "Platform" group the merchant has to open.
        id: 'platform',
        name: 'Platform',
        icon: Squares2X2Icon,
        isSectionLabel: true,
        children: [
          {
            // First in the group, per the prototype's Platform order: the merchant configures the
            // team before inspecting what it did.
            id: 'settings-hub',
            name: 'Settings',
            href: '/platform/settings',
            icon: SettingsNavIcon,
            // Now the ONLY row that owns this path, so no hand-off: it used to yield the highlight to
            // Operation → Setting whenever that card was the hash target, guarded by the operation unlock
            // so staging and prod would not end up with no active row at all. That row is gone, and
            // every card on the Hub — Oscar's included — is reached through this one.
            current: location.pathname === '/platform/settings',
          },
          {
            id: 'task-center',
            name: 'Task Center',
            href: '/tasks',
            icon: TaskCenterNavIcon,
            // PRD #4: red dot while any task is unread — 新建或状态变化过而商家还没展开过那一行;
            // clears row by row as they are expanded, and cannot be cleared from here (「不能直接清」).
            // [infra1.5 验收 #5①] 红点和数字二选一，不同时挂：有待处理就只显示数字（数字 = 需要
            // 商家处理的事项数），没有待处理才用红点提示「有没看过的任务」。
            unreadDot: attention.taskCenterAttention && attention.pendingCount === 0,
            // [Infra 1.5 #19] 待处理数角标。审批中心废除后「待处理」靠顶部筛选与这个角标暴露,
            // 不做默认视图置顶（会和 #18 的 agent 分组冲突）。
            //
            // 复用既有的 `badge` 通道，不新增样式：它本来就是一枚白底紫字的小胶囊，和 Store Manager
            // 那颗红点是两件事（红点=有东西坏了，角标=有几件事等你处理），同时出现也读得通。
            // Capped at 99+: dev has 282 pending approvals sitting in the queue, and a four-digit
            // count blows the pill past the row's label. The exact number is on the page.
            badge:
              attention.pendingCount > 0
                ? attention.pendingCount > 99
                  ? '99+'
                  : String(attention.pendingCount)
                : undefined,
            current: location.pathname === '/tasks',
          },
          {
            id: 'material-hub',
            name: 'Material Hub',
            href: '/platform/materials',
            icon: MaterialNavIcon,
            current: location.pathname === '/platform/materials',
          },
        ],
      },
    ],
    // location.hash was a dependency while the Operation → Setting row's `current` was hash-based:
    // navigating /platform/settings -> #operation-settings changed nothing else, so without it the memo
    // never recomputed and that row stayed stale. No row reads the hash any more.
    [
      location.pathname,
      hasShopifyInstallation,
      unlockedAgents,
      unlockedAgentTypes,
      customerAvatar,
      // YEP-803: recompute when any red-dot source changes, so dots (and the section-label
      // aggregate) light and clear without waiting for a navigation.
      unreadSignature,
      storeManagerUnread,
      attention.attentionByAgent,
      attention.taskCenterAttention,
      attention.pendingCount,
      attentionNavState,
    ]
  );

  // Auto-expand filtered items when searching (recurses the full 3-level tree:
  // any ancestor of a matching item is expanded so the match is visible).
  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const itemsToExpand: string[] = [];

      // Returns true if this item or any descendant matches; collects the ids
      // of every ancestor branch on a matching path.
      const walk = (item: NavigationItem): boolean => {
        const selfMatches = item.name.toLowerCase().includes(query);
        const childMatches = (item.children ?? []).map(walk).some(Boolean);
        if (item.children && item.children.length > 0 && (selfMatches || childMatches)) {
          itemsToExpand.push(item.id);
        }
        return selfMatches || childMatches;
      };
      navigation.forEach(walk);

      // Return the same array when nothing is new: `navigation` is rebuilt whenever attention state
      // changes, and a fresh array here would re-render, rebuild it, and loop.
      setExpandedItems(prev =>
        itemsToExpand.every(id => prev.includes(id)) ? prev : [...new Set([...prev, ...itemsToExpand])]
      );
    }
  }, [searchQuery, navigation]);

  const toggleExpanded = (itemId: string) => {
    if (sidebarCollapsed) return; // Don't allow expansion when sidebar is collapsed
    setExpandedItems(prev =>
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  // Recursively render a collapsed-sidebar hover dropdown (supports the 3-level
  // tree: branch nodes become muted headers, leaves become clickable links).
  const renderCollapsedChildren = (children: NavigationItem[]): React.ReactNode =>
    children.map(child =>
      child.children && child.children.length > 0 ? (
        <div key={child.id}>
          <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-white/50">
            {child.name}
          </div>
          {renderCollapsedChildren(child.children)}
        </div>
      ) : child.disabled || !child.href ? (
        <div key={child.id} className="px-3 py-2.5 text-sm italic text-white/40">
          {child.name}
        </div>
      ) : (
        <button
          key={child.id}
          onClick={() => handleNavigation(child.href!, child.navState)}
          className={`w-full text-left px-3 py-2.5 text-sm transition-colors ${
            child.current ? 'bg-white/20 text-white font-medium' : 'text-white/90 hover:bg-white/10'
          }`}
        >
          {child.name}
        </button>
      )
    );

  // YEP-803: does this row, or any row nested under it, currently show an unread dot? Drives the
  // section-label aggregate.
  const hasUnreadDescendant = (item: NavigationItem): boolean =>
    !!item.unreadDot || (item.children ?? []).some(hasUnreadDescendant);

  const renderNavigationItem = (item: NavigationItem, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const isGroupHeader = depth === 0 && item.isGroup;

    // A section LABEL (AI TEAM / PLATFORM): a heading, never a collapsing container. Its children
    // render as top-level rows (depth 0) so they carry their own icons and avatars and are always
    // visible. The heading itself is clickable ONLY when it has an `href` (AI TEAM → the roster
    // page); otherwise it is a plain, non-interactive title (PLATFORM). In the collapsed rail there
    // is no room for a heading, so the children render as a bare icon stack.
    if (item.isSectionLabel) {
      const kids = (item.children ?? []).map(child => renderNavigationItem(child, 0));
      if (sidebarCollapsed) {
        // No heading in the rail, so the aggregate has nowhere to live — each child row still
        // carries its own dot in the icon stack, which is the collapsed equivalent.
        return (
          <div key={item.id} className="space-y-1">
            {kids}
          </div>
        );
      }
      const labelClassName =
        'text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:text-white/50';
      return (
        <div key={item.id}>
          <div className="flex items-center gap-1.5 px-3 pb-1 pt-4">
            {item.href ? (
              // Clickable heading (AI TEAM → roster). A real <button> so it reads as interactive and
              // tests can find it by role; styled like the label plus a hover cue.
              <button
                type="button"
                onClick={() => handleNavigation(item.href!)}
                className={`${labelClassName} text-left transition hover:text-gray-600 lg:hover:text-white`}
              >
                {item.name}
              </button>
            ) : (
              <span className={labelClassName}>{item.name}</span>
            )}
            {/* [infra1.5 验收 #5②] 分组标题（AI TEAM / PLATFORM）不挂红点 —— 点只挂在具体的行上。 */}
          </div>
          <div className="space-y-1">{kids}</div>
        </div>
      );
    }
    // depth 0 = top-level rows under a section label (Store Manager, Marketing, Settings…). depth ≥ 1
    // = an agent's own sub-pages (Maya / Marketing Hub under Marketing), which indent clearly under
    // their agent row. The old depth-1 = pl-4 case was Store Manager / Settings sitting inside a
    // group; those are depth-0 rows now, so depth 1 means a sub-page and takes the deeper indent.
    const paddingLeft = sidebarCollapsed ? 'pl-0' : depth === 0 ? 'pl-3' : 'pl-10';

    /**
     * Leading marker (design 23754).
     *
     * Two kinds, and depth no longer decides between them:
     *  - An AVATAR, for the agent chat rows — Maya / Oscar / Anna's face identifies the person.
     *  - Otherwise the row's own ICON, rendered bare (no chip) at every level, so a level-1 header
     *    (Marketing target, Support chat, Operation dataflow) and a level-2 page (Marketing Hub box,
     *    Setting gear) read as the same icon language as Home and Store Manager.
     *
     * The old generic bullet is gone: every row the design draws carries a real icon or a face, so a
     * fallback dot only ever appeared where an icon was missing — which now reads as a bug, not a
     * marker.
     */
    const renderMarker = () => {
      if (item.avatar) {
        return (
          <span className="relative flex h-7 w-7 flex-shrink-0 items-center justify-center">
            <span
              className="flex h-7 w-7 items-end justify-center overflow-hidden rounded-full"
              style={{ backgroundColor: item.avatarBg }}
            >
              <img
                src={item.avatar}
                alt={item.name}
                style={item.avatarTransform ? { transform: item.avatarTransform } : undefined}
                className={`h-full w-full object-cover object-top ${item.locked ? 'opacity-40' : ''}`}
              />
            </span>
            {item.locked && (
              <span className="absolute -bottom-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#1a1523] ring-1 ring-white/20">
                <LockClosedIcon className="h-2.5 w-2.5 text-white/80" />
              </span>
            )}
          </span>
        );
      }
      return <item.icon className="h-5 w-5 flex-shrink-0" />;
    };

    const labelClass = isGroupHeader ? 'truncate min-w-0 font-semibold' : 'truncate min-w-0';

    // Non-navigating placeholder row (e.g. Marketing "Coming soon").
    if (item.disabled) {
      if (sidebarCollapsed) return null;
      return (
        <div
          key={item.id}
          className={`${paddingLeft} pr-3 py-2 text-xs italic lg:text-white/40 text-gray-400`}
        >
          {item.name}
        </div>
      );
    }

    // For collapsed sidebar, only show icons for top-level items
    if (sidebarCollapsed && depth === 0) {
      if (hasChildren) {
        return (
          <div
            key={item.id}
            className="relative"
            onMouseEnter={e => {
              if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
              }
              const rect = e.currentTarget.getBoundingClientRect();
              setButtonPosition({
                top: rect.top,
                left: rect.right + 5,
              });
              setHoveredItem(item.id);
            }}
            onMouseLeave={() => {
              hoverTimeoutRef.current = setTimeout(() => {
                setHoveredItem(null);
                setButtonPosition(null);
              }, 100);
            }}
          >
            <button
              className={`relative w-full flex items-center justify-center p-2.5 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-white/10 ${
                item.current ? 'text-white bg-white/20' : 'text-white/80 hover:text-white'
              }`}
            >
              {item.avatar ? (
                <span className="relative">
                  <span
                    className="flex h-6 w-6 items-end justify-center overflow-hidden rounded-full"
                    style={{ backgroundColor: item.avatarBg }}
                  >
                    <img
                      src={item.avatar}
                      alt={item.name}
                      style={item.avatarTransform ? { transform: item.avatarTransform } : undefined}
                      className={`h-full w-full object-cover object-top ${item.locked ? 'opacity-40' : ''}`}
                    />
                  </span>
                  {item.locked && (
                    <span className="absolute -bottom-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full bg-[#1a1523] ring-1 ring-white/20">
                      <LockClosedIcon className="h-2 w-2 text-white/80" />
                    </span>
                  )}
                </span>
              ) : (
                <item.icon className="h-5 w-5 flex-shrink-0" />
              )}
            </button>

            {/* Hover dropdown using Portal */}
            {hoveredItem === item.id &&
              buttonPosition &&
              createPortal(
                <div
                  className="fixed w-56 bg-gradient-to-br from-purple-600 to-[#3e1dd7] backdrop-blur-xl border border-white/10 shadow-xl rounded-lg"
                  style={{
                    zIndex: 9999,
                    top: buttonPosition.top,
                    left: buttonPosition.left + 10,
                  }}
                  onMouseEnter={() => {
                    if (hoverTimeoutRef.current) {
                      clearTimeout(hoverTimeoutRef.current);
                    }
                    setHoveredItem(item.id);
                  }}
                  onMouseLeave={() => {
                    hoverTimeoutRef.current = setTimeout(() => {
                      setHoveredItem(null);
                      setButtonPosition(null);
                    }, 100);
                  }}
                >
                  {/* Header */}
                  <div className="px-3 py-2.5 border-b border-white/10">
                    <div className="flex items-center gap-2">
                      <item.icon className="h-4 w-4 text-white/70" />
                      <p className="text-sm font-semibold text-white">{item.name}</p>
                    </div>
                  </div>

                  {/* Menu items — locked groups can't be opened; nudge to upgrade. */}
                  <div className="py-1">
                    {item.locked ? (
                      <button
                        onClick={() => handleNavigation('/settings/subscription')}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-white/90 hover:bg-white/10"
                      >
                        <LockClosedIcon className="h-4 w-4" />
                        Upgrade to unlock
                      </button>
                    ) : (
                      renderCollapsedChildren(item.children!)
                    )}
                  </div>
                </div>,
                document.body
              )}
          </div>
        );
      } else {
        return (
          <Tooltip key={item.id} content={item.name} placement="right">
            <button
              onClick={() => handleNavigation(item.href!, item.navState)}
              className={`flex items-center justify-center p-2.5 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-white/10 ${
                item.current ? 'text-white bg-white/20' : 'text-white/80 hover:text-white'
              }`}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {(item.badge || item.unreadDot) && (
                // Collapsed has no room for a label AND a dot, so one marker carries both. Red when
                // there is unread content, because "something new for you" outranks "this feature is
                // new" — the first is actionable, the second is decoration.
                <div
                  className={`absolute top-1 right-1 h-2 w-2 rounded-full ${
                    item.unreadDot ? 'bg-red-500' : 'bg-orange-500'
                  }`}
                  aria-label={item.unreadDot ? 'unread' : undefined}
                ></div>
              )}
            </button>
          </Tooltip>
        );
      }
    }

    // Don't render children when sidebar is collapsed
    if (sidebarCollapsed && depth > 0) {
      return null;
    }

    return (
      <div key={item.id}>
        {hasChildren ? (
          <Tooltip
            content="Upgrade to unlock this employee."
            isDisabled={!item.locked}
            placement="bottom"
            showArrow
            offset={10}
            classNames={{
              base: 'before:bg-white',
              content:
                'max-w-[190px] px-3.5 py-2.5 text-center text-[13px] font-medium leading-snug text-gray-700 bg-white rounded-xl shadow-xl border border-gray-100',
            }}
          >
            <button
              onClick={() => {
                if (item.locked) {
                  handleNavigation('/settings/subscription');
                  return;
                }
                // A group that ALSO has an href navigates and opens, rather than only toggling.
                // "AI Team" needs this: it is the roster page and the container for the three
                // agents, and making it a pure group header would leave /ai-team reachable from
                // nowhere in the nav.
                if (item.href) {
                  setExpandedItems(prev => [...new Set([...prev, item.id])]);
                  handleNavigation(item.href, item.navState);
                  return;
                }
                toggleExpanded(item.id);
              }}
              data-tour={item.id}
              className={`w-full flex items-center justify-between ${paddingLeft} pr-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                sidebarCollapsed
                  ? `hover:bg-white/10 ${item.current ? 'text-white bg-white/20' : 'text-white/80 hover:text-white'}`
                  : `lg:hover:bg-white/10 hover:bg-gray-100 ${
                      isGroupHeader && isExpanded ? 'lg:bg-white/5 bg-gray-50/60' : ''
                    } ${
                      item.current
                        ? 'lg:text-white lg:bg-white/20 text-purple-600 bg-purple-50'
                        : 'lg:text-white/80 lg:hover:text-white text-gray-700 hover:text-gray-900'
                    }`
              }`}
            >
              <div className="flex items-center space-x-3 min-w-0">
                {renderMarker()}
                {!sidebarCollapsed && (
                  <>
                    <span className={`${labelClass} ${item.locked ? 'opacity-50' : ''}`}>
                      {item.name}
                    </span>
                    {/* [infra1.5 验收 #5③] agent 分组行（Marketing / Operation / Support）：
                        收起时子行看不见，就把子行的红点汇到分组行上；展开后点回到子行自己身上，
                        不重复挂。 */}
                    {(item.unreadDot || (!isExpanded && hasUnreadDescendant(item))) && (
                      <span
                        className="ml-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500"
                        aria-label="unread"
                      />
                    )}
                    {item.badge && (
                      <span className="ml-1.5 px-1.5 py-0.5 text-[10px] leading-none font-semibold rounded-full bg-white/90 text-purple-700 flex-shrink-0">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </div>
              {!sidebarCollapsed &&
                (item.locked ? (
                  <LockClosedIcon className="h-4 w-4 flex-shrink-0 opacity-70" />
                ) : isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 flex-shrink-0" />
                ))}
            </button>
          </Tooltip>
        ) : (
          <button
            onClick={() => handleNavigation(item.href!, item.navState)}
            data-tour={item.id}
            className={`w-full flex items-center justify-between ${paddingLeft} pr-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
              sidebarCollapsed
                ? `hover:bg-white/10 ${item.current ? 'text-white bg-white/20' : 'text-white/80 hover:text-white'}`
                : `lg:hover:bg-white/10 hover:bg-gray-100 ${
                    item.current
                      ? 'lg:text-white lg:bg-white/20 text-purple-600 bg-purple-50'
                      : 'lg:text-white/80 lg:hover:text-white text-gray-700 hover:text-gray-900'
                  }`
            }`}
          >
            <div className="flex items-center space-x-3 min-w-0">
              {renderMarker()}
              {!sidebarCollapsed && (
                <>
                  <span className={labelClass}>{item.name}</span>
                  {item.unreadDot && (
                    <span
                      className="ml-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500"
                      aria-label="unread"
                    />
                  )}
                  {item.badge && (
                    <span className="ml-1.5 px-1.5 py-0.5 text-[10px] leading-none font-semibold rounded-full bg-white/90 text-purple-700 flex-shrink-0">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </div>
          </button>
        )}

        {hasChildren && isExpanded && !item.locked && !sidebarCollapsed && (
          <div className="mt-1 space-y-1">
            {item.children!.map(child => renderNavigationItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Check if mobile (< 1024px)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const isIOS = useIsIOS();
  const safeHeight = useSafeViewportHeight();
  const navRef = React.useRef<HTMLElement>(null);
  const [scrollInfo, setScrollInfo] = useState({
    thumbTop: 0,
    thumbHeight: 0,
    showScrollbar: false,
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Custom scrollbar for mobile
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || !isMobile || sidebarCollapsed) {
      setScrollInfo({ thumbTop: 0, thumbHeight: 0, showScrollbar: false });
      return;
    }

    const updateScrollbar = () => {
      const { scrollTop, scrollHeight, clientHeight } = nav;
      const canScroll = scrollHeight > clientHeight + 5; // Add small threshold

      if (canScroll) {
        const thumbHeight = Math.max((clientHeight / scrollHeight) * clientHeight, 30);
        const maxScrollTop = scrollHeight - clientHeight;
        const thumbTop =
          maxScrollTop > 0 ? (scrollTop / maxScrollTop) * (clientHeight - thumbHeight) : 0;

        setScrollInfo({
          thumbTop,
          thumbHeight,
          showScrollbar: true,
        });
      } else {
        setScrollInfo({ thumbTop: 0, thumbHeight: 0, showScrollbar: false });
      }
    };

    // Initial update
    updateScrollbar();

    // Listen for scroll events
    nav.addEventListener('scroll', updateScrollbar);
    window.addEventListener('resize', updateScrollbar);

    // Use MutationObserver to detect DOM changes (menu expand/collapse)
    const mutationObserver = new MutationObserver(() => {
      // Delay to allow DOM to settle after animation
      requestAnimationFrame(updateScrollbar);
    });
    mutationObserver.observe(nav, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    // Use ResizeObserver to detect nav size changes (when Settings expand/collapse)
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateScrollbar);
    });
    resizeObserver.observe(nav);

    return () => {
      nav.removeEventListener('scroll', updateScrollbar);
      window.removeEventListener('resize', updateScrollbar);
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [isMobile, sidebarCollapsed]);

  return (
    <div
      className={`relative flex flex-col h-full border-r transition-all duration-300 ${sidebarCollapsed ? 'overflow-visible' : 'overflow-hidden'} ${
        sidebarCollapsed ? 'w-16' : 'w-64'
      } lg:translate-x-0 ${
        sidebarCollapsed
          ? 'bg-white/5 backdrop-blur-xl border-white/10'
          : 'lg:bg-white/5 lg:backdrop-blur-xl lg:border-white/10 lg:border-gray-200 lg:shadow-2xl fixed inset-y-0 left-0 lg:relative z-50 mobile-sidebar-override'
      }`}
      style={isMobile && !sidebarCollapsed ? { height: safeHeight } : undefined}
    >
      {/* Mobile sidebar background image - using img tag to fix iOS rendering issue */}
      {isMobile && !sidebarCollapsed && (
        <img
          src={HomeBgImage}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-left-top"
          style={{
            zIndex: -1,
            transform: 'translateZ(0)', // Force GPU layer for iOS
            WebkitTransform: 'translateZ(0)',
          }}
        />
      )}
      {/* Mobile sidebar overlay */}
      {isMobile && !sidebarCollapsed && (
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundColor: 'rgba(11, 5, 27, 0.3)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            transform: 'translateZ(0)', // Force GPU layer for iOS
            WebkitTransform: 'translateZ(0)',
          }}
        />
      )}

      {/* Content wrapper - relative z-10 to appear above overlay */}
      <div className="relative z-10 flex flex-col h-full">
        {/* Logo and Toggle */}
        <div
          className={`flex items-center justify-between px-3 py-4 ${
            sidebarCollapsed ? 'border-b border-white/10' : 'lg:border-b lg:border-white/10'
          }`}
        >
          {!sidebarCollapsed && (
            <div className="flex items-center">
              <img src={YepAILogo} alt="YepAI" className="h-8 w-auto" />
            </div>
          )}

          <Button
            isIconOnly
            variant="light"
            size="sm"
            onPress={toggleSidebar}
            aria-label={
              sidebarCollapsed ? 'Expand navigation sidebar' : 'Close navigation sidebar'
            }
            className={`h-11 min-w-11 lg:h-8 lg:min-w-8 ${
              sidebarCollapsed
                ? 'text-white/80 hover:text-white hover:bg-white/10'
                : 'lg:text-white/80 lg:hover:text-white lg:hover:bg-white/10 text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            {sidebarCollapsed ? (
              <Bars3Icon className="h-5 w-5" />
            ) : (
              <XMarkIcon className="h-5 w-5" />
            )}
          </Button>
        </div>

        {/* Search - only show when expanded, hidden on mobile */}
        {!sidebarCollapsed && (
          <div className="px-3 py-4 hidden lg:block">
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              startContent={
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 lg:text-white/60 lg:group-focus-within:text-purple-400 focus-within:text-gray-500" />
              }
              classNames={{
                base: 'lg:bg-white/10 lg:border-white/20 bg-gray-100 border-gray-300 group',
                input:
                  'lg:text-white lg:placeholder:text-white/60 lg:focus:placeholder:text-purple-400 text-gray-900 placeholder:text-gray-500',
                inputWrapper:
                  'lg:bg-white/10 lg:border-white/20 lg:hover:bg-white/15 lg:focus-within:bg-white/15 lg:focus-within:border-white/40 bg-gray-100 border-gray-300 hover:border-gray-400 focus-within:border-purple-500 group',
              }}
              size="sm"
            />
          </div>
        )}

        {/* Navigation */}
        <div className="relative flex-1 min-h-0">
          <nav
            ref={navRef}
            className={`h-full px-3 space-y-1 overflow-y-auto overflow-x-hidden ${sidebarCollapsed ? 'scrollbar-thin sidebar-nav-scroll' : ''} ${isMobile && !sidebarCollapsed ? 'hide-native-scrollbar' : 'scrollbar-thin'}`}
            style={
              isMobile && !sidebarCollapsed
                ? ({
                    scrollbarWidth: 'none', // Firefox
                    msOverflowStyle: 'none', // IE
                    WebkitOverflowScrolling: 'touch',
                  } as React.CSSProperties)
                : undefined
            }
          >
            {filterNavigationItems(navigation).map(item => renderNavigationItem(item))}
          </nav>

          {/* Custom scrollbar for mobile */}
          {isMobile && !sidebarCollapsed && scrollInfo.showScrollbar && (
            <div
              className="absolute top-0 right-1 bottom-0 w-1 pointer-events-none"
              style={{ zIndex: 20 }}
            >
              {/* Track */}
              <div className="absolute inset-0 bg-white/10 rounded-full" />
              {/* Thumb */}
              <div
                className="absolute right-0 w-1 bg-white/50 rounded-full transition-transform duration-75"
                style={{
                  top: scrollInfo.thumbTop,
                  height: scrollInfo.thumbHeight,
                }}
              />
            </div>
          )}
        </div>

        {/*
          No Integrations section here (it used to sit below the tree, in both expanded and collapsed
          form). The Settings Hub's Integrations card is the merchant's list of channels now, grouped by
          function and showing what is connected.

          The PAGES under /integrations survive on purpose — they are not a duplicate of that card, they
          are its downstream: Setup on a row is `<Link to={row.detailPath ?? '/integrations'}>`, so the
          card owns the overview and those pages own the authorisation. Same shape as /flow, reached
          only from inside Anna's card.
        */}

        {/* Upgrade Plan card */}
        {!sidebarCollapsed && <UpgradePlanCard />}

        {/* User Section */}
        {!sidebarCollapsed && (
          <div
            className={`px-3 py-4 border-t ${sidebarCollapsed ? 'border-white/10' : 'lg:border-white/10 border-gray-200'}`}
          >
            <Dropdown
              placement="top-start"
              classNames={{
                content: '!bg-white !backdrop-blur-none shadow-xl border border-gray-100 z-[60]',
              }}
            >
              <DropdownTrigger>
                <Button
                  variant="light"
                  className={`w-full h-auto p-3 justify-start transition-colors ${
                    sidebarCollapsed
                      ? 'hover:bg-white/10'
                      : 'lg:hover:bg-white/10 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-2 w-full">
                    <Avatar
                      src={user?.avatar}
                      alt={user?.name || user?.firstName}
                      size="sm"
                      className="w-8 h-8 flex-shrink-0"
                      showFallback
                      name={user?.firstName?.charAt(0) || user?.email?.charAt(0)}
                    />
                    <div className="flex-1 min-w-0 text-left overflow-hidden">
                      <p
                        className={`text-sm font-medium truncate ${
                          sidebarCollapsed ? 'text-white' : 'lg:text-white text-gray-900'
                        }`}
                      >
                        {user?.firstName && user?.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user?.name || user?.email?.split('@')[0] || 'User'}
                      </p>
                      <p
                        className={`text-xs truncate mobile-email-truncate ${
                          sidebarCollapsed ? 'text-white/60' : 'lg:text-white/60 text-gray-500'
                        }`}
                      >
                        {user?.email || 'user@example.com'}
                      </p>
                    </div>
                    {/* Desktop: ChevronUp, Mobile: Logout button */}
                    <ChevronUpIcon
                      className={`h-4 w-4 flex-shrink-0 hidden lg:block ${
                        sidebarCollapsed ? 'text-white/60' : 'text-white/60'
                      }`}
                    />
                    <div
                      className="lg:hidden ml-auto p-2 -mr-2 rounded-lg active:bg-white/20"
                      onClick={e => {
                        e.stopPropagation();
                        handleLogout();
                      }}
                    >
                      <ArrowRightStartOnRectangleIcon className="h-5 w-5 flex-shrink-0 text-white" />
                    </div>
                  </div>
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label="User actions"
                className="w-64 rounded-xl bg-white"
                itemClasses={{ base: 'data-[hover=true]:bg-transparent' }}
              >
                <DropdownItem
                  key="header"
                  isReadOnly
                  textValue="Account"
                  className="cursor-default"
                >
                  <div className="flex items-center gap-2.5">
                    <Avatar
                      src={user?.avatar}
                      size="sm"
                      className="h-8 w-8 flex-shrink-0"
                      showFallback
                      name={user?.firstName?.charAt(0) || user?.email?.charAt(0)}
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {user?.firstName && user?.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user?.name || user?.email?.split('@')[0] || 'User'}
                      </p>
                      <p className="truncate text-xs text-gray-500">{user?.email}</p>
                    </div>
                  </div>
                </DropdownItem>
                <DropdownItem
                  key="plan-card"
                  isReadOnly
                  textValue="Plan"
                  className="cursor-default"
                >
                  <div className="rounded-xl bg-gray-50 p-2.5">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                        <SparklesIcon className="h-4 w-4 text-[#6b33ff]" />
                        {planLabel}
                      </span>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleNavigation('/settings/subscription');
                        }}
                        className="rounded-md bg-[#6b33ff] px-2.5 py-1 text-xs font-semibold text-white transition hover:brightness-110"
                      >
                        Upgrade
                      </button>
                    </div>
                    {/* Legacy-cohort users are gifted Growth without a credit
                      balance — hide the Credits row for them. */}
                    {!isLegacy && (
                      <>
                        <div className="my-2 h-px bg-gray-200" />
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            handleNavigation('/settings/credits');
                          }}
                          className="flex w-full items-center justify-between text-sm"
                        >
                          <span className="flex items-center gap-1.5 font-medium text-gray-700">
                            <SparklesIcon className="h-3.5 w-3.5 text-[#6b33ff]" /> Credits
                          </span>
                          <span className="flex items-center gap-1 font-semibold text-gray-900">
                            {availableCredits.toLocaleString()}
                            <ChevronRightIcon className="h-3.5 w-3.5 text-gray-400" />
                          </span>
                        </button>
                      </>
                    )}
                  </div>
                </DropdownItem>
                <DropdownItem
                  key="logout"
                  color="danger"
                  className="text-rose-500 data-[hover=true]:bg-rose-50"
                  startContent={<ArrowRightStartOnRectangleIcon className="h-4 w-4" />}
                  onPress={handleLogout}
                >
                  Log out
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        )}

        {/* Collapsed user avatar */}
        {sidebarCollapsed && (
          <div className="px-3 py-4 border-t border-white/10">
            <Dropdown placement="right-start">
              <DropdownTrigger>
                <Button
                  isIconOnly
                  variant="light"
                  className="w-full flex justify-center hover:bg-white/10 transition-colors"
                >
                  <Avatar
                    src={user?.avatar}
                    alt={user?.name || user?.firstName}
                    size="sm"
                    className="w-8 h-8"
                    showFallback
                    name={user?.firstName?.charAt(0) || user?.email?.charAt(0)}
                  />
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="User actions" className="w-56">
                <DropdownItem
                  key="user-info"
                  className="h-14 gap-2"
                  textValue="User info"
                  isReadOnly
                >
                  <div className="flex items-center gap-2">
                    <Avatar
                      src={user?.avatar}
                      alt={user?.name || user?.firstName}
                      size="sm"
                      className="w-8 h-8"
                      showFallback
                      name={user?.firstName?.charAt(0) || user?.email?.charAt(0)}
                    />
                    <div className="flex flex-col min-w-0 flex-1 pr-2">
                      <p className="text-sm font-medium truncate">
                        {user?.firstName && user?.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user?.name || user?.email?.split('@')[0] || 'User'}
                      </p>
                      <p className="text-xs text-default-500 truncate">
                        {user?.email || 'user@example.com'}
                      </p>
                    </div>
                  </div>
                </DropdownItem>

                <DropdownItem
                  key="logout"
                  color="danger"
                  startContent={<ArrowRightStartOnRectangleIcon className="h-4 w-4" />}
                  onPress={handleLogout}
                >
                  Sign Out
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        )}
      </div>
      {/* End content wrapper */}
    </div>
  );
}
